import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * MtoFactorsModal — per-estimate factors + shop/field settings.
 *
 *  - Shop/field ratio (shop %) and shop & field labour rates (CAD).
 *  - Up to 20 labour factors, each with a multiplier and an applies-to scope
 *    (shop / field / both). Pick from suggested factors or add custom ones.
 *  - Every change recomputes the estimate server-side; onApplied() refreshes the grid.
 */
const APPLIES_OPTS = [
  { value: 'shop', label: 'Shop' },
  { value: 'field', label: 'Field' },
  { value: 'both', label: 'Both' },
];

// Row-scope options: which MTO rows the factor multiplies. 'lb' includes XLB.
const BORE_OPTS = [
  { value: 'all', label: 'SB + LB (all)' },
  { value: 'sb', label: 'Small bore only' },
  { value: 'lb', label: 'Large bore only' },
];

// Free-column scope: any MTO column the user may filter a factor by.
const SCOPE_COLUMNS = [
  { value: '', label: '(no column filter)' },
  { value: 'iso_sheet_number', label: 'ISO Sheet #' },
  { value: 'line_number', label: 'Line #' },
  { value: 'spool_number', label: 'Spool #' },
  { value: 'part_number', label: 'Part #' },
  { value: 'description', label: 'Description' },
  { value: 'size', label: 'Size' },
  { value: 'rating', label: 'Rating' },
  { value: 'end_type', label: 'End Type' },
  { value: 'seam_type', label: 'Seam Type' },
  { value: 'join_type', label: 'Joint Type' },
  { value: 'piping_class', label: 'Piping Class' },
  { value: 'fluid_code', label: 'Fluid Code' },
  { value: 'system', label: 'System' },
  { value: 'subsystem', label: 'Subsystem' },
  { value: 'class', label: 'Class' },
  { value: 'priority', label: 'Priority' },
  { value: 'fabrication_shop', label: 'Fab Shop' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'engineering_code', label: 'Eng Code' },
  { value: 'wbs', label: 'WBS' },
  { value: 'sov', label: 'SOV' },
  { value: 'um', label: 'UM' },
];

const EMPTY_SCOPE = { scope_bore: 'all', scope_type: '', scope_material: '', scope_sch: '', scope_column: '', scope_value: '' };

// Human-readable one-line summary of a factor's row scope.
export function scopeSummary(f) {
  const parts = [];
  const bore = String(f.scope_bore || 'all').toLowerCase();
  if (bore === 'sb') parts.push('SB only');
  if (bore === 'lb') parts.push('LB only');
  if (f.scope_type) parts.push(`type: ${f.scope_type}`);
  if (f.scope_material) parts.push(`material: ${f.scope_material}`);
  if (f.scope_sch) parts.push(`sch: ${f.scope_sch}`);
  if (f.scope_column && f.scope_value) {
    const col = SCOPE_COLUMNS.find((c) => c.value === f.scope_column);
    parts.push(`${col ? col.label : f.scope_column}: ${f.scope_value}`);
  }
  return parts.length ? parts.join(' · ') : 'all rows';
}

/**
 * Row-scope editor for one factor: bore bucket, type / material / sch
 * contains-filters (comma-separated = any-of) and a free column + value.
 * onChange patches local state while typing; onCommit persists (blur/select).
 */
const ScopeEditor = ({ scope, onChange, onCommit }) => {
  const txt = (key, label, placeholder) => (
    <div style={{ flex: '1 1 120px', minWidth: 110 }}>
      <label style={lbl}>{label}</label>
      <input value={scope[key] || ''} placeholder={placeholder}
        onChange={e => onChange({ [key]: e.target.value })}
        onBlur={e => onCommit({ [key]: e.target.value })}
        style={{ ...cellInput, width: '100%' }} />
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 150px' }}>
          <label style={lbl}>Bore (SB / LB)</label>
          <select value={scope.scope_bore || 'all'}
            onChange={e => { onChange({ scope_bore: e.target.value }); onCommit({ scope_bore: e.target.value }); }}
            style={{ ...cellInput, width: '100%' }}>
            {BORE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {txt('scope_type', 'Type contains', 'e.g. Pipe, Elbow')}
        {txt('scope_material', 'Material contains', 'e.g. SS, A106')}
        {txt('scope_sch', 'Sch contains', 'e.g. 40, 80')}
        <div style={{ flex: '0 0 150px' }}>
          <label style={lbl}>Any column</label>
          <select value={scope.scope_column || ''}
            onChange={e => { onChange({ scope_column: e.target.value }); onCommit({ scope_column: e.target.value }); }}
            style={{ ...cellInput, width: '100%' }}>
            {SCOPE_COLUMNS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {scope.scope_column ? txt('scope_value', 'Column value contains', 'value…') : null}
      </div>
      <p style={{ margin: '.45rem 0 0', fontSize: '.72rem', color: '#9ca3af' }}>
        The factor multiplies a row's labour only when <strong>all</strong> filled-in filters match. Comma-separate alternatives (e.g. "SS, CS" = SS or CS). Matching is case-insensitive "contains". Large bore includes XLB.
      </p>
    </div>
  );
};

const MtoFactorsModal = ({ isOpen, onClose, estimateNumber, jobNumber, onApplied }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [factors, setFactors] = useState([]);
  const [predefined, setPredefined] = useState([]);
  const [maxFactors, setMaxFactors] = useState(20);

  // Settings (shop_ratio kept as a 0..100 percent in the UI)
  const [shopPct, setShopPct] = useState(60);
  const [shopRate, setShopRate] = useState('');
  const [fieldRate, setFieldRate] = useState('');

  // Add-factor form
  const [newName, setNewName] = useState('');
  const [newMult, setNewMult] = useState('1');
  const [newApplies, setNewApplies] = useState('both');
  const [newScope, setNewScope] = useState({ ...EMPTY_SCOPE });
  const [showNewScope, setShowNewScope] = useState(false);
  // Which existing factor rows have their scope editor expanded.
  const [scopeOpen, setScopeOpen] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!estimateNumber) return;
    setLoading(true);
    setError('');
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/factors?estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (resp.data.success) {
        const s = resp.data.settings || {};
        setShopPct(s.shop_ratio == null ? 60 : Math.round(Number(s.shop_ratio) * 1000) / 10);
        setShopRate(s.labour_rate_shop_cad == null ? '' : String(s.labour_rate_shop_cad));
        setFieldRate(s.labour_rate_field_cad == null ? '' : String(s.labour_rate_field_cad));
        setFactors(resp.data.factors || []);
        setPredefined(resp.data.predefined || []);
        setMaxFactors(resp.data.maxFactors || 20);
      } else {
        setError(resp.data.message || 'Failed to load factors');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load factors');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, estimateNumber]);

  useEffect(() => { if (isOpen && estimateNumber) load(); }, [isOpen, estimateNumber, load]);

  const products = useMemo(() => {
    let shop = 1, field = 1;
    for (const f of factors) {
      const m = Number(f.multiplier);
      if (!Number.isFinite(m)) continue;
      if (f.applies_to === 'shop' || f.applies_to === 'both') shop *= m;
      if (f.applies_to === 'field' || f.applies_to === 'both') field *= m;
    }
    return { shop, field };
  }, [factors]);

  const afterChange = () => { onApplied && onApplied(); };

  const saveSettings = async () => {
    setBusy(true); setError('');
    try {
      const pct = Math.min(100, Math.max(0, Number(shopPct) || 0));
      const resp = await authenticatedApiCall(instance, accounts, 'PUT', '/mto/settings', {
        estimate_number: estimateNumber,
        job_number: jobNumber,
        shop_ratio: pct / 100,
        labour_rate_shop_cad: shopRate === '' ? null : Number(shopRate),
        labour_rate_field_cad: fieldRate === '' ? null : Number(fieldRate),
      });
      if (resp.data.success) { await load(); afterChange(); }
      else setError(resp.data.message || 'Failed to save settings');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save settings');
    } finally { setBusy(false); }
  };

  const addFactor = async () => {
    if (!newName.trim()) { setError('Enter a factor name'); return; }
    if (factors.length >= maxFactors) { setError(`Maximum of ${maxFactors} factors reached`); return; }
    setBusy(true); setError('');
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/factors', {
        estimate_number: estimateNumber, job_number: jobNumber,
        name: newName.trim(), multiplier: Number(newMult) || 1, applies_to: newApplies,
        is_predefined: predefined.some(p => p.name === newName.trim()),
        scope_bore: newScope.scope_bore, scope_type: newScope.scope_type,
        scope_material: newScope.scope_material, scope_sch: newScope.scope_sch,
        scope_column: newScope.scope_column, scope_value: newScope.scope_value,
      });
      if (resp.data.success) {
        setNewName(''); setNewMult('1'); setNewApplies('both');
        setNewScope({ ...EMPTY_SCOPE }); setShowNewScope(false);
        await load(); afterChange();
      } else setError(resp.data.message || 'Failed to add factor');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to add factor');
    } finally { setBusy(false); }
  };

  const saveFactor = async (f, patch) => {
    setBusy(true); setError('');
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'PUT', `/mto/factors/${f.id}`, {
        estimate_number: estimateNumber, job_number: jobNumber, ...patch,
      });
      if (resp.data.success) { await load(); afterChange(); }
      else setError(resp.data.message || 'Failed to update factor');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update factor');
    } finally { setBusy(false); }
  };

  const deleteFactor = async (f) => {
    if (!window.confirm(`Remove factor "${f.name}"?`)) return;
    setBusy(true); setError('');
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'DELETE', `/mto/factors/${f.id}?estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (resp.data.success) { await load(); afterChange(); }
      else setError(resp.data.message || 'Failed to delete factor');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete factor');
    } finally { setBusy(false); }
  };

  // Update a factor row locally (controlled inputs), persisting on blur/change.
  const patchLocal = (id, patch) => setFactors(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));

  if (!isOpen) return null;

  const usedNames = new Set(factors.map(f => f.name));
  const suggestions = predefined.filter(p => !usedNames.has(p.name));

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>⚙️ Factors &amp; Shop/Field Settings</h2>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={content}>
          {!estimateNumber ? (
            <p style={{ color: '#6b7280' }}>Open a single estimate to manage its factors.</p>
          ) : loading ? (
            <p>Loading…</p>
          ) : (
            <>
              {/* Settings */}
              <section style={card}>
                <h3 style={h3}>Shop / Field split</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <label style={lbl}>Shop %</label>
                  <input type="range" min="0" max="100" value={shopPct} onChange={e => setShopPct(Number(e.target.value))} style={{ flex: 1, minWidth: 140 }} />
                  <input type="number" min="0" max="100" step="0.5" value={shopPct} onChange={e => setShopPct(Number(e.target.value))} style={numInput} />
                  <span style={{ color: '#6b7280', fontSize: '.85rem' }}>Field {Math.round((100 - (Number(shopPct) || 0)) * 10) / 10}%</span>
                </div>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '.75rem' }}>
                  <div>
                    <label style={lbl}>Shop Labour Rate (CAD/hr)</label>
                    <input type="number" step="0.01" value={shopRate} onChange={e => setShopRate(e.target.value)} placeholder="—" style={numInputWide} />
                  </div>
                  <div>
                    <label style={lbl}>Field Labour Rate (CAD/hr)</label>
                    <input type="number" step="0.01" value={fieldRate} onChange={e => setFieldRate(e.target.value)} placeholder="—" style={numInputWide} />
                  </div>
                </div>
                <button onClick={saveSettings} disabled={busy} style={primaryBtn}>{busy ? 'Saving…' : 'Save settings & recompute'}</button>
              </section>

              {/* Factors */}
              <section style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <h3 style={h3}>Labour factors ({factors.length}/{maxFactors})</h3>
                  <span style={{ fontSize: '.8rem', color: '#6b7280' }}>
                    Shop ×<strong>{products.shop.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}</strong>
                    {' · '}Field ×<strong>{products.field.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}</strong>
                  </span>
                </div>
                <p style={{ margin: '0 0 .5rem', fontSize: '.8rem', color: '#6b7280' }}>Factors multiply <strong>labour only</strong> (never material). They feed the Grand Total (After Factors) columns.</p>

                {factors.length === 0 && <p style={{ color: '#9ca3af', fontSize: '.85rem' }}>No factors yet — add one below.</p>}
                {factors.length > 0 && (
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={th}>Factor</th>
                        <th style={{ ...th, width: 100 }}>Multiplier</th>
                        <th style={{ ...th, width: 110 }}>Applies to</th>
                        <th style={{ ...th, width: 170 }}>Rows it applies to</th>
                        <th style={{ ...th, width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {factors.map(f => {
                        const open = scopeOpen.has(f.id);
                        return (
                        <React.Fragment key={f.id}>
                        <tr>
                          <td style={td}>{f.name}</td>
                          <td style={td}>
                            <input type="number" step="0.0001" value={f.multiplier}
                              onChange={e => patchLocal(f.id, { multiplier: e.target.value })}
                              onBlur={e => saveFactor(f, { multiplier: Number(e.target.value) || 1 })}
                              style={cellInput} />
                          </td>
                          <td style={td}>
                            <select value={f.applies_to} onChange={e => { patchLocal(f.id, { applies_to: e.target.value }); saveFactor(f, { applies_to: e.target.value }); }} style={cellInput}>
                              {APPLIES_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td style={td}>
                            <button onClick={() => setScopeOpen(prev => { const s = new Set(prev); s.has(f.id) ? s.delete(f.id) : s.add(f.id); return s; })}
                              title="Edit which rows this factor applies to"
                              style={{ ...scopeChip, background: scopeSummary(f) === 'all rows' ? '#f3f4f6' : '#eef2ff', color: scopeSummary(f) === 'all rows' ? '#6b7280' : '#4338ca' }}>
                              {open ? '▾ ' : '▸ '}{scopeSummary(f)}
                            </button>
                          </td>
                          <td style={td}>
                            <button onClick={() => deleteFactor(f)} title="Remove" style={iconBtn}>🗑</button>
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={5} style={{ ...td, background: '#f8fafc', padding: '.6rem .75rem' }}>
                              <ScopeEditor
                                scope={{
                                  scope_bore: f.scope_bore || 'all', scope_type: f.scope_type || '',
                                  scope_material: f.scope_material || '', scope_sch: f.scope_sch || '',
                                  scope_column: f.scope_column || '', scope_value: f.scope_value || '',
                                }}
                                onChange={(patch) => patchLocal(f.id, patch)}
                                onCommit={(patch) => saveFactor(f, patch)}
                              />
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Add factor */}
                {factors.length < maxFactors && (
                  <div style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-end', marginTop: '.75rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <label style={lbl}>Name</label>
                      <input list="mto-factor-suggestions" value={newName} onChange={e => {
                        const v = e.target.value; setNewName(v);
                        const p = predefined.find(x => x.name === v);
                        if (p) setNewApplies(p.applies_to);
                      }} placeholder="Pick a suggestion or type a custom name" style={{ ...cellInput, width: '100%' }} />
                      <datalist id="mto-factor-suggestions">
                        {suggestions.map(p => <option key={p.name} value={p.name} />)}
                      </datalist>
                    </div>
                    <div>
                      <label style={lbl}>Multiplier</label>
                      <input type="number" step="0.0001" value={newMult} onChange={e => setNewMult(e.target.value)} style={{ ...cellInput, width: 90 }} />
                    </div>
                    <div>
                      <label style={lbl}>Applies to</label>
                      <select value={newApplies} onChange={e => setNewApplies(e.target.value)} style={{ ...cellInput, width: 100 }}>
                        {APPLIES_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <button onClick={addFactor} disabled={busy} style={primaryBtn}>+ Add</button>
                  </div>
                )}
                {factors.length < maxFactors && (
                  <div style={{ marginTop: '.5rem' }}>
                    <button onClick={() => setShowNewScope(v => !v)}
                      style={{ ...scopeChip, background: showNewScope ? '#eef2ff' : '#f3f4f6', color: showNewScope ? '#4338ca' : '#6b7280' }}>
                      {showNewScope ? '▾' : '▸'} Limit to specific rows (SB/LB, type, material, sch, any column) — {scopeSummary(newScope)}
                    </button>
                    {showNewScope && (
                      <div style={{ marginTop: '.5rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: '.6rem .75rem' }}>
                        <ScopeEditor scope={newScope} onChange={(patch) => setNewScope(s => ({ ...s, ...patch }))} onCommit={() => {}} />
                      </div>
                    )}
                  </div>
                )}
              </section>

              {error && <div style={errorBox}>{error}</div>}
            </>
          )}
        </div>

        <div style={footer}>
          <button onClick={onClose} style={cancelBtn}>Close</button>
        </div>
      </div>
    </div>
  );
};

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 };
const modal = { background: '#fff', borderRadius: 8, width: 780, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,.3)' };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f8f9fa' };
const closeBtn = { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666', lineHeight: 1 };
const content = { padding: '1.25rem', overflowY: 'auto' };
const footer = { display: 'flex', justifyContent: 'flex-end', gap: '.75rem', padding: '.85rem 1.25rem', borderTop: '1px solid #e5e7eb', background: '#f8f9fa' };
const card = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '1rem', marginBottom: '1rem' };
const h3 = { margin: '0 0 .5rem', fontSize: '.95rem' };
const lbl = { display: 'block', fontSize: '.75rem', color: '#6b7280', marginBottom: 2 };
const numInput = { width: 70, padding: '.4rem', border: '1px solid #ccc', borderRadius: 4, textAlign: 'center' };
const numInputWide = { width: 150, padding: '.4rem', border: '1px solid #ccc', borderRadius: 4 };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' };
const th = { textAlign: 'left', padding: '.35rem .5rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 };
const td = { padding: '.3rem .5rem', borderBottom: '1px solid #f1f1f1' };
const cellInput = { padding: '.35rem', border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box' };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '.95rem' };
const scopeChip = { border: '1px solid #e5e7eb', borderRadius: 999, padding: '.25rem .6rem', fontSize: '.72rem', cursor: 'pointer', textAlign: 'left', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const primaryBtn = { marginTop: '.75rem', padding: '.5rem 1rem', border: 'none', borderRadius: 4, background: '#6366f1', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '.85rem' };
const cancelBtn = { padding: '.5rem 1.25rem', border: '1px solid #ccc', borderRadius: 4, background: '#fff', cursor: 'pointer' };
const errorBox = { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 4, padding: '.6rem .8rem', fontSize: '.85rem' };

export default MtoFactorsModal;
